import { z } from '@repo/schema'
import { getDataWrapperSchema } from '@repo/schema/data-wrapper'
import { GetRoleSchema } from '@repo/schema/role'
import { GetUserSchema } from '@repo/schema/user'
import { up } from 'up-fetch'

import type { Role, User } from './types'

const GetAuthUserSchema = getDataWrapperSchema(
  GetUserSchema.extend({
    permissions: z.array(z.string()).optional(),
    roles: z.array(GetRoleSchema).optional(),
  }),
)

const GetUserRolesSchema = z.object({
  data: z.array(GetRoleSchema),
})

const GetUserPermissionsSchema = z.object({
  data: z.object({
    direct: z.array(z.string()),
    via_roles: z.array(z.string()),
  }),
})

const parsePermissionsWithFallback = (value: unknown): { direct: string[]; viaRoles: string[] } => {
  const parsed = GetUserPermissionsSchema.safeParse(value)
  if (!parsed.success) {
    console.error(parsed.error)
    return { direct: [], viaRoles: [] }
  }

  const { data } = parsed.data
  return {
    direct: data.direct ?? [],
    viaRoles: data.via_roles ?? [],
  }
}

interface FetchAuthUserOptions {
  apiUrl: string
  cache?: RequestCache
  source: string
  token: string
}

export const fetchAuthUser = async ({
  apiUrl,
  cache,
  source,
  token,
}: FetchAuthUserOptions): Promise<null | User> => {
  const upFetch = up(fetch, () => ({
    baseUrl: apiUrl,
    cache,
    headers: {
      Accept: 'application/json',
      'App-Source': source,
      Authorization: `Bearer ${token}`,
    },
  }))

  try {
    const data = await upFetch('/users/me', {
      params: {
        i: 'company.account|company.country|company.profile.status|company.profile.type',
      },
      schema: GetAuthUserSchema,
    })

    const user: User = { ...data, id: Number(data.id) }

    // If the me endpoint already includes roles/permissions, use them.
    if (Array.isArray(data.roles)) {
      user.roles = data.roles
    }

    if (Array.isArray(data.permissions)) {
      user.permissions = data.permissions
    }

    const needsRoles = !user.roles
    const needsPermissions = !user.permissions

    const [directPermissionData, fetchedRoles] = await Promise.all([
      fetchUserPermissions({
        apiUrl,
        source,
        token,
      }),
      needsRoles
        ? fetchUserRoles({
            apiUrl,
            source,
            token,
          })
        : Promise.resolve([]),
    ])

    user.directPermissions = directPermissionData.direct

    if (needsPermissions) {
      user.permissions = [
        ...new Set([...directPermissionData.direct, ...directPermissionData.viaRoles]),
      ]
    }

    if (needsRoles) {
      user.roles = fetchedRoles
    }

    // Ensure arrays exist for downstream checks
    if (!user.roles) {
      user.roles = []
    }
    if (!user.permissions) {
      user.permissions = []
    }
    if (!user.directPermissions) {
      user.directPermissions = []
    }

    return user
  } catch {
    return null
  }
}

const fetchUserRoles = async ({
  apiUrl,
  source,
  token,
}: {
  apiUrl: string
  source: string
  token: string
}): Promise<Role[]> => {
  const upFetch = up(fetch, () => ({
    baseUrl: apiUrl,
    headers: {
      Accept: 'application/json',
      'App-Source': source,
      Authorization: `Bearer ${token}`,
    },
  }))

  try {
    const rolesData = await upFetch(`/users/me/roles`, { schema: GetUserRolesSchema })

    return rolesData.data ?? []
  } catch (e) {
    console.error(e)

    return []
  }
}

const fetchUserPermissions = async ({
  apiUrl,
  source,
  token,
}: {
  apiUrl: string
  source: string
  token: string
}): Promise<{ direct: string[]; viaRoles: string[] }> => {
  const upFetch = up(fetch, () => ({
    baseUrl: apiUrl,
    headers: {
      Accept: 'application/json',
      'App-Source': source,
      Authorization: `Bearer ${token}`,
    },
  }))

  try {
    const permissionResponse = await upFetch('/users/me/permissions')
    return parsePermissionsWithFallback(permissionResponse)
  } catch (e) {
    console.error(e)

    return { direct: [], viaRoles: [] }
  }
}
